import { useCallback } from "react";
import { toast } from "react-hot-toast";

import { ApiService, LISCase, LISCaseList, LISTask } from "pwapi";

import { useRequestAfterEverySiteLoad } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { listCases } from "app/actions/caseActions";
import { useAppSelector } from "app/hooks";
import { workWithTaskIn } from "app/selectors/workSelectors";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useGrossingOnScanCallbacks from "hooks/useGrossingOnScanCallbacks";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AppSpinner from "ui/components/AppSpinner";
import CaseListItem from "ui/lists/CaseListItem";
import CaseReadyItem from "ui/lists/CaseReadyItem";
import { specimenSort } from "util/sortFunctions";
import {
  getLongestRunningTaskfromWorkProcesses,
  taskLongevity,
} from "util/workProcessUtils";

import TaskCount from "../ui/TaskCount";

const Case = ({
  pathCase,
  onScanDocument,
}: {
  pathCase: LISCaseList;
  onScanDocument: (id: number) => void;
}) => {
  if (!pathCase.id) return null;

  const selectedStation = useSelectedLabStation();
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  const longestTask = taskLongevity(
    getLongestRunningTaskfromWorkProcesses(
      pathCase.work_processes,
      LISTask.type.GROSSING
    ),
    true
  );

  const label = `${pathCase.specimens.length} Specimen${
    pathCase.specimens.length === 1 ? "" : "s"
  }`;

  const specimens = [...pathCase.specimens].sort(specimenSort);

  const onAltClick = useCallback(() => {
    if (!pathCase.id || !lisAltClickScanning) return;

    // The only reason we were loading documents before was for this alt-click functionality, which is really just for dev
    // So instead of continuing to serialize that data we just make an extra request here to get document IDs

    ApiService.retrieveCaseWithWork({
      parentLookupLabStationId: selectedStation.id,
      id: pathCase.id,
    }).then((response: LISCase) => {
      const document = response.documents.find(
        (document) => document.category === "Requisition-Form"
      );
      if (!document?.id) {
        toast.error("Unable to find Requisition Form");
      } else {
        onScanDocument(document.id);
      }
    });
  }, [pathCase.id]);

  return (
    <CaseListItem
      id={pathCase.id}
      label={label}
      stationType={LISTask.type.GROSSING}
      timeInStation={longestTask}
      onClick={(e) => {
        if (e.altKey) onAltClick();
      }}
      className="Scannable"
    >
      {specimens.map((s) => (
        <h2 key={s.id}>{s.name}</h2>
      ))}
    </CaseListItem>
  );
};

const GrossingStationList = () => {
  const lab = useSelectedLab();
  const selectedLabStation = useSelectedLabStation();
  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });
  const grossingCases = useAppSelector(
    workWithTaskIn("grossing", selectedLabStation?.clia_certificate_id)
  );
  const rackedCases = useAppSelector(
    workWithTaskIn(
      ["embedding", "processing"],
      selectedLabStation?.clia_certificate_id
    )
  );
  const allowIncompleteScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "allowIncompleteScanning")
  );

  const { onScanBlock, onScanDocument, onScanSpecimen } =
    useGrossingOnScanCallbacks();

  useBarcodeScanner(({ object_id, object_type }) => {
    toast.dismiss();
    if (object_type === "document") {
      onScanDocument(object_id);
    } else if (object_type === "specimen") {
      if (
        grossingCases
          .flatMap((c) => c.specimens.map((s) => s.id))
          .includes(object_id)
      ) {
        if (lab?.settings?.paperless_grossing?.active) {
          onScanSpecimen(object_id, true);
          return;
        } else {
          toast.error(`Please Scan Requisition Form First`);
          return;
        }
      } else if (lab?.settings?.paperless_grossing?.active) {
        // If the case has no work with grossing tasks, paperless_grossing labs
        // ... still need to be able to scan a specimen to return to grossing
        onScanSpecimen(object_id, true);
        return;
      }
      return false;
    } else if (allowIncompleteScanning && object_type === "block") {
      onScanBlock(object_id);
    } else {
      return false;
    }
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Station Split">
      <main>
        <header className="Header">
          <figure className="Logo">
            <img src="/logo.svg" alt="" />
          </figure>
          <p>
            {selectedLabStation.name}
            {selectedLabStation.description &&
              selectedLabStation.name !== selectedLabStation.description && (
                <> ({selectedLabStation.description})</>
              )}
          </p>
          <TaskCount
            cases={grossingCases}
            stationType={selectedLabStation.type}
          />
        </header>

        <div className="WorkList">
          {grossingCases.map((w) => (
            <Case key={w.id} pathCase={w} onScanDocument={onScanDocument} />
          ))}
        </div>
      </main>

      <aside>
        <h4>Ready for Processing</h4>
        {rackedCases.map((c) => (
          <CaseReadyItem key={c.id} readyCase={c} />
        ))}
      </aside>
    </div>
  );
};

export default GrossingStationList;
